import { Controller } from "@hotwired/stimulus"

// Auto-opens Bootstrap modals when inserted via Turbo Stream.
//
// Adds canonical stacked-modal support — Bootstrap 5 only supports one modal
// at a time; for nested modals we set explicit z-indexes ourselves:
//
//   modal_z    = 1055 + (level * 20)      // 1055, 1075, 1095, ...
//   backdrop_z = modal_z - 5              // sits just below its modal
//
// This is the standard pattern (cf. tsono.org Bootstrap 5 stacked-modal
// guide and the KajPe/bootstrapmodal repo). With it:
//   - Both modals stay fully opaque.
//   - Each modal has its own backdrop directly behind it.
//   - The parent stays visible underneath the nested modal's backdrop.
//   - Closing the nested modal returns focus to the parent cleanly.
//
// Body classes (`modal-open`, padding-right, overflow:hidden) are preserved
// while any modal is still open — Bootstrap's default cleanup wipes them
// when *any* modal closes, which breaks the parent's scroll lock.
export default class extends Controller {
  connect() {
    const modalEl = this.element.classList.contains('modal') ? this.element : this.element.querySelector('.modal')
    if (!modalEl) return

    const openModals = Array.from(document.querySelectorAll('.modal.show')).filter(m => m !== modalEl)
    const isNested = openModals.length > 0
    const level = openModals.length // 0 = base modal, 1 = first nested, ...

    const modal = new bootstrap.Modal(modalEl, {
      backdrop: isNested ? true : 'static',
      keyboard: false
    })
    this.modalInstance = modal
    this._level = level

    if (isNested) {
      // Set this modal's z-index BEFORE show() so Bootstrap doesn't render
      // it briefly at the default 1055 (which is below the parent's backdrop).
      modalEl.style.zIndex = `${1055 + level * 20}`

      // Once Bootstrap creates this modal's backdrop, slot its z-index below
      // this modal but above the parent's backdrop. Bootstrap appends the
      // backdrop to body as the latest .modal-backdrop, so we grab it via
      // querySelectorAll(...).last after `shown.bs.modal`.
      modalEl.addEventListener('shown.bs.modal', () => {
        const backdrops = document.querySelectorAll('.modal-backdrop')
        const ownBackdrop = backdrops[backdrops.length - 1]
        if (ownBackdrop) {
          ownBackdrop.style.zIndex = `${1055 + level * 20 - 5}`
        }
      }, { once: true })
    }

    modal.show()

    const wrapper = modalEl.closest('[data-controller*="modal-auto-open"]')
    modalEl.addEventListener('hidden.bs.modal', () => {
      // Bootstrap removes its own backdrop on hide. Restore body-scroll-lock
      // if any other modal is still open — Bootstrap wipes the body classes
      // unconditionally, which would let the page underneath start scrolling
      // while the parent modal is still up.
      const stillOpen = document.querySelectorAll('.modal.show')
      if (stillOpen.length > 0) {
        document.body.classList.add('modal-open')
      } else {
        document.body.classList.remove('modal-open')
        document.body.style.overflow = ''
        document.body.style.paddingRight = ''
        // Belt-and-suspenders: remove orphan backdrops if any.
        document.querySelectorAll('.modal-backdrop').forEach(b => b.remove())
      }

      // Drop our injected wrapper so appended modals don't pile up in DOM.
      if (wrapper && wrapper !== document.body) {
        wrapper.remove()
      }
    }, { once: true })
  }

  disconnect() {
    if (this.modalInstance) {
      this.modalInstance.dispose()
    }
    // Safety net: orphan-backdrop cleanup only when nothing's still open.
    if (!document.querySelector('.modal.show')) {
      document.querySelectorAll('.modal-backdrop').forEach(b => b.remove())
      document.body.classList.remove('modal-open')
      document.body.style.overflow = ''
      document.body.style.paddingRight = ''
    }
  }
}
